/* ══════════════════════════════════════════════════════════════════════════
   edgeos.js — Edge OS control panel (twelve-wave design surface)

   Soft≠Live:
   - Live buses: Transport · Borrow · μDock Sessions (w1 honesty) · Workbench · estate paint · Board W8
   - Designed desks (no invent metrics): Vision W12 · Locate W5 · Models W3 ·
     Runtime W4 · Mesh W10 · Store W11 · Security W9 · Spatial W7
   - Page215 dual survey ∥ command via GET /edge/status retained
   Soft≠Live Page93/104 Re-read CTAs retained on Live path desks
   ══════════════════════════════════════════════════════════════════════════ */
import { useState, useMemo, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, _F, Ico, _Sec, _KV, _Card, _Field, Input, Alert,
         SPRING, SOFT, _FADE, go} from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { presenceDiagnoseView } from "./fleet-live.js";
import { ConnectionWatch } from "./connection-watch.js";
import {
  openLiveSession,
  closeLiveSession,
  renewLiveSession,
  getLiveSession,
} from "./udock-api.js";
import { RemuxPullDesk } from "./udock-live-desk.js";
import { GrantLatticePlayer } from "./udock-grant-lattice-player.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import {
  edgeDevicesOf,
  liveSessionsOf,
  siblingsOf,
  capabilityCatalogOf,
  scheduledJobsOf,
  boardTelemetryViewOf,
  boardLoopViewOf,
  edgeWaveHonestyOf,
  udockLiveHonestyOf,
  fmtLeft,
} from "./edgeos-live.js";
import { EDGE_PANEL_NAV, EDGE_SUB_IDS, edgeNavItemOf, edgeSoleOf } from "./edgeos-nav.js";
import {
  EstateWaveDesk,
  VisionWaveDesk,
  LocateWaveDesk,
  ModelsWaveDesk,
  RuntimeWaveDesk,
  BoardWaveDesk,
  MeshWaveDesk,
  StoreWaveDesk,
  SecurityWaveDesk,
  SpatialWaveDesk,
} from "./edgeos-wave-desks.js";
import { EdgeSkillsWarehouseDesk } from "./edge-skills-warehouse.js";
import { isLocalHost } from "./pathseal.js";
import {
  EdgeDevicePick,
  EdgePlate,
  EdgeNote,
  EdgeActRow,
  EdgeMeters,
  EdgeKV,
  EdgeEmpty,
} from "./edgeos-chrome.js";

const _TABS = EDGE_SUB_IDS.map((id) => {
  const it = edgeNavItemOf(id);
  return { id, label: (it && it.label) || id };
});

/** Operator maturity · Soft≠Live; no invent. Wave ids stay in title attrs. */
function maturityLabel(m) {
  if (m === "live") return "Live";
  if (m === "deferred") return "Hold";
  return "Plan";
}

function maturityChip(m) {
  if (m === "live") return "Live";
  if (m === "deferred") return "hold";
  return "plan";
}

const SESSION_TTLS = [60, 300, 900, 1800];
const REBOOT_DELAYS = [60, 300, 900, 3600];
const SESSION_WHYS = [
  "CMC Edge watch grant",
  "CMC Edge · operator verification",
  "CMC Edge · alarm follow-up view",
];

/** Soft≠Live Page215: missing contract ops must not crash the desk. */
function safeEdgePath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__edge_status") return "/edge/status";
    if (op === "get__devices") return "/devices";
    if (op === "get__udock_status") return "/udock/status";
    if (op === "get__devices_capabilities") return "/devices/capabilities";
    if (op === "get__devices_p_presence-diagnose") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/presence-diagnose" : "";
    }
    if (op === "get__devices_p_siblings-preview") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/siblings-preview" : "";
    }
    if (op === "get__devices_p_live-sessions") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/live-sessions" : "";
    }
    if (op === "get__devices_p_scheduled-jobs") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/scheduled-jobs" : "";
    }
    if (op === "get__devices_p_board-telemetry") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/board-telemetry" : "";
    }
    if (op === "get__devices_p_board-loop") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) + "/board-loop" : "";
    }
    return "";
  }
}

/** Path-free operator errors — Soft invent path paint forbidden; never paint wire paths or soft_debt codes. */
export function softEdgeErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|insufficient|not_authorized|operation denied/.test(low)) {
    return "Your account cannot do that on Edge. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That node or grant is not available.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|offline|disabled|unarmed/.test(low)) {
    return "This Edge desk is not open right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/devices\/|\/edge\/|\/udock\/|http|GET |POST |PATCH |status_path|soft_debt/.test(low)) {
    return "That action could not be completed.";
  }
  return "That action could not be completed.";
}

/** Pure: seat chips from Live Edge posture (Page215). Fail-closed · === true only. */
export function edgeSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.device_paint_max);
  const command = p.command_plane_allowed === true;
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    surveyAllowed: p.survey_plane_allowed === true,
    commandAllowed: command,
    /* Fail-closed: never inherit write seats when BE omits the field. */
    reProbeAllowed: p.re_probe_allowed === true,
    packagePushAllowed: p.package_push_allowed === true,
    sessionWriteAllowed: p.session_write_allowed === true,
    workbenchWriteAllowed: p.workbench_write_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    devicePaintMax:
      Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 24,
  };
}

function DevicePick(props) {
  return html`<${EdgeDevicePick} ...${props}/>`;
}

function useEdgeDevices(surveyOn) {
  const live = useApi(surveyOn ? safeEdgePath("get__devices", { query: { limit: 80 } }) : null);
  const devices = useMemo(() => edgeDevicesOf(live.data), [live.data]);
  const [deviceId, setDeviceId] = useState("");
  useEffect(() => {
    if (!deviceId && devices.length) setDeviceId(devices[0].id);
  }, [devices, deviceId]);
  return { live, devices, deviceId, setDeviceId };
}

export default function EdgeOS({ sub, toast }){
  const at = EDGE_SUB_IDS.includes(sub) ? sub : "estate";
  const navItem = edgeNavItemOf(at);
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const posture = useApi(safeEdgePath("get__edge_status"));
  const post = posture.data || {};
  /* Fail-closed while load or closed product plane — never invent open Edge seats. */
  const seat = posture.loading
    ? edgeSeatOf({ enabled: false, principal_role: role, limits: { device_paint_max: 24 } })
    : edgeSeatOf(post);
  const surveyOn = !posture.loading && seat.enabled === true && seat.surveyAllowed === true;
  const reg = useApi(surveyOn ? safeEdgePath("get__devices", { query: { limit: 80 } }) : null);
  const devices = useMemo(() => edgeDevicesOf(reg.data), [reg.data]);
  const paintMax = seat.devicePaintMax || 24;
  const painted = devices.slice(0, paintMax);
  const onlineN = painted.filter((d) => d.online).length;

  const reReadShell = () => {
    if (posture.loading) {
      toast && toast("Confirming Edge seat…", "warn");
      return;
    }
    if (!seat.reReadAllowed) {
      toast && toast("Re-read is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    reg.reload && reg.reload();
    toast && toast("Edge posture re-read", "go");
  };

  const stage = () => {
    if (at === "estate") {
      return html`<${EstateWaveDesk}
        devices=${devices}
        surveyOn=${surveyOn}
        loading=${reg.loading}
        toast=${toast}
        seat=${seat}
      />`;
    }
    if (at === "link") return html`<${Transport} toast=${toast} seat=${seat}/>`;
    if (at === "share") return html`<${Borrowing} toast=${toast} seat=${seat}/>`;
    if (at === "skills") {
      return html`<${EdgeSkillsWarehouseDesk}
        mode="edge" toast=${toast} seat=${seat}/>`;
    }
    if (at === "tunnel") return html`<${Sessions} toast=${toast} seat=${seat}/>`;
    if (at === "bench") return html`<${Workbench} toast=${toast} seat=${seat}/>`;
    if (at === "vision") return html`<${VisionWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "locate") return html`<${LocateWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "models") return html`<${ModelsWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "runtime") return html`<${RuntimeWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "board") return html`<${BoardWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "mesh") return html`<${MeshWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "store") return html`<${StoreWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "security") return html`<${SecurityWaveDesk} toast=${toast} seat=${seat}/>`;
    if (at === "spatial") return html`<${SpatialWaveDesk}/>`;
    return html`<${Alert} tone="warn" title="Unknown Edge desk" say=${at}/>`;
  };

  const local = isLocalHost();
  const mat = (navItem && navItem.maturity) || "live";
  const stageTitle = (navItem && navItem.label) || "Edge";
  const stageHint = (navItem && navItem.hint) || "Edge seat";
  const stageSole = edgeSoleOf(at);

  return html`
    <div className="sheet edge-desk edge-desk--liquid edge-desk--os edge-shell desk-craft"
      data-cmc-edge="page50" data-cmc-page="page93-edge"
      data-cmc-page-bench="page104-edge-bench"
      data-cmc-residual="page215-edge"
      data-edge-ia="edge-os-v2"
      data-edge-craft="v450"
      data-flat="1"
      data-kinetic="1"
      data-edge-cap=${at}
      data-edge-sub=${at}
      data-local=${local ? "1" : "0"}
      data-mat=${mat}>
      <div className="wrap edge-os edge-os--fill edge-shell__in" data-viewport-fill="1" data-flat="1">
        <header className="edge-shell__head edge-shell__head--flat edge-shell__head--kinetic"
          data-edge-head="v450" aria-label="Edge desk">
          <i className="edge-shell__glow" aria-hidden="true" />
          <div className="edge-shell__copy">
            <div className="edge-shell__titleline">
              <span className="edge-shell__mark">Edge</span>
                <${M.h1}
                key=${"title-" + at}
                className="ph edge-shell__title"
                initial=${{ opacity: 0, y: 5 }}
                animate=${{ opacity: 1, y: 0 }}
                transition=${{ type: "spring", stiffness: 480, damping: 36, mass: 0.55 }}>
                ${stageTitle}
              <//>
              <span className="edge-shell__chips" aria-label="Seat posture">
                <span className="edge-shell__chip" data-local=${local ? "1" : "0"}>
                  <i className="edge-shell__chip-dot" aria-hidden="true" />
                  ${local ? "Local desk" : "Live"}
                </span>
                <span className="edge-shell__chip" data-mat=${mat}>${maturityLabel(mat)}</span>
              </span>
            </div>
            <${M.p}
              key=${"lede-" + at}
              className="psub edge-shell__lede"
              initial=${{ opacity: 0, x: -6 }}
              animate=${{ opacity: 1, x: 0 }}
              transition=${SOFT}>
              <b className="edge-shell__sole">${stageSole}</b>
              ${stageHint ? html`<span className="edge-shell__hint"> · ${stageHint}</span>` : null}
            <//>
          </div>
          <div className="edge-shell__meter-rail">
            <div className="edge-shell__kpis edge-os__kpis" role="group" aria-label="Posture KPIs">
              <${M.div} className="edge-shell__kpi edge-os__kpi" data-on=${seat.surveyAllowed ? "1" : "0"}
                initial=${{ opacity: 0, y: 6 }} animate=${{ opacity: 1, y: 0 }}
                transition=${{ ...SOFT, delay: 0.02 }}>
                <span>Read</span>
                <b>${seat.surveyAllowed ? "open" : "sealed"}</b>
              <//>
              <${M.div} className="edge-shell__kpi edge-os__kpi" data-on=${seat.commandAllowed ? "1" : "0"}
                initial=${{ opacity: 0, y: 6 }} animate=${{ opacity: 1, y: 0 }}
                transition=${{ ...SOFT, delay: 0.05 }}>
                <span>Act</span>
                <b>${seat.commandAllowed ? "open" : "sealed"}</b>
              <//>
              <${M.div} className="edge-shell__kpi edge-os__kpi" data-on=${surveyOn && painted.length ? "1" : "0"}
                initial=${{ opacity: 0, y: 6 }} animate=${{ opacity: 1, y: 0 }}
                transition=${{ ...SOFT, delay: 0.08 }}>
                <span>Nodes</span>
                <b>${!surveyOn ? "—" : (reg.loading ? "…" : painted.length)}</b>
              <//>
              <${M.div} className="edge-shell__kpi edge-os__kpi" data-on=${surveyOn && onlineN ? "1" : "0"}
                initial=${{ opacity: 0, y: 6 }} animate=${{ opacity: 1, y: 0 }}
                transition=${{ ...SOFT, delay: 0.11 }}>
                <span>Online</span>
                <b>${!surveyOn ? "—" : (reg.loading ? "…" : onlineN)}</b>
              <//>
            </div>
          </div>
        </header>

        <div className="btn-row edge-shell__acts edge-shell__acts--kinetic" role="toolbar" aria-label="Edge shell">
          <${M.button} type="button"
            className="btn btn--ghost btn--sm edge-os__act edge-os__reread desk-craft__act"
            disabled=${!seat.reReadAllowed}
            whileHover=${seat.reReadAllowed ? { y: -1 } : undefined}
            whileTap=${seat.reReadAllowed ? { scale: 0.97 } : undefined}
            transition=${SPRING}
            onClick=${reReadShell}
            title="Re-read Edge posture and device register">
            <${Ico} d="refresh" w=${14}/>
            <span>Re-read</span>
          <//>
          ${at === "skills"
            ? html`<${M.button} type="button" className="btn btn--ghost btn--sm desk-craft__act"
                whileHover=${{ y: -1 }} whileTap=${{ scale: 0.97 }} transition=${SPRING}
                onClick=${() => go("/fleet/firmware")}
                title="Board flash is Fleet-only · never from Skills">
                Leave Edge · Board updates
              <//>`
            : at === "estate"
              ? html`<${M.button} type="button" className="btn btn--ghost btn--sm desk-craft__act"
                  whileHover=${{ y: -1 }} whileTap=${{ scale: 0.97 }} transition=${SPRING}
                  onClick=${() => go("/ops/devices")}
                  title="Site plant inventory lives under Ops · not Edge Nodes">
                  Site inventory · Ops Plant
                <//>`
              : at === "bench"
                ? html`<${M.button} type="button" className="btn btn--ghost btn--sm desk-craft__act"
                    whileHover=${{ y: -1 }} whileTap=${{ scale: 0.97 }} transition=${SPRING}
                    onClick=${() => go("/ops/alarms")}
                    title="Site alarm triage lives under Ops Alerts">
                    Site alarms · Ops Alerts
                  <//>`
                : at === "vision" || at === "tunnel"
                  ? html`<${M.button} type="button" className="btn btn--ghost btn--sm desk-craft__act"
                      whileHover=${{ y: -1 }} whileTap=${{ scale: 0.97 }} transition=${SPRING}
                      onClick=${() => go("/ops/cameras")}
                      title="Operator frames & snaps live under Ops Cams">
                      Frames · Ops Cams
                    <//>`
                  : null}
        </div>

        ${posture.loading
          ? html`<div className="edge-os__banners edge-shell__banners">
              <${Alert} tone="info" title="Confirming Edge seat"
                say="Checking what this account may read and command on Edge…"/>
            </div>`
          : (!seat.surveyAllowed || !seat.commandAllowed
          ? html`<div className="edge-os__banners edge-shell__banners">
              ${!seat.surveyAllowed
                ? html`<${Alert} tone="warn" title="Read seat closed"
                    say="Device list and KPIs need an open read seat on this account."/>`
                : null}
              ${!seat.commandAllowed
                ? html`<${Alert} tone="info" title="Act seat closed"
                    say=${softEdgeErrorOf(seat.detail) || "Commands stay sealed on this seat."}/>`
                : null}
            </div>`
          : null)}

        <!-- Flat instrument strip · no nested glass left rail -->
        <nav className="edge-os__rail edge-shell__nav edge-shell__nav--flat" aria-label="Edge desks"
          data-local=${local ? "1" : "0"} data-cap-map="v430" data-flat="1">
          <div className="edge-shell__nav-in edge-os__nav">
            ${EDGE_PANEL_NAV.map(
              (g) => html`<div key=${g.group} className="edge-os__group set__grp edge-shell__strip"
                data-group=${g.group.toLowerCase()} role="tablist"
                aria-label=${g.group}>
                <span className="edge-os__g set__grpl edge-shell__strip-k">${g.group}</span>
                <div className="edge-shell__strip-row">
                  ${g.items.map(
                    (it) => html`<button
                      key=${it.id}
                      type="button"
                      role="tab"
                      className="edge-os__item edge-shell__tab"
                      data-on=${at === it.id ? "1" : "0"}
                      data-mat=${it.maturity}
                      data-cap=${it.id}
                      aria-selected=${at === it.id ? "true" : "false"}
                      title=${`${it.sole || it.hint}${(it.wave ? " · " + it.wave : "")}`}
                      onClick=${() => go(it.path)}>
                      <span className="edge-os__item-lab">
                        <b>${it.label}</b>
                      </span>
                      ${it.maturity === "live"
                        ? null
                        : html`<em className="edge-os__item-wave edge-shell__soft"
                            data-on=${at === it.id ? "1" : "0"}>${maturityChip(it.maturity)}</em>`}
                    </button>`,
                  )}
                </div>
              </div>`,
            )}
          </div>
        </nav>

        <div className="edge-shell__work edge-shell__work--flat">
          <div className="edge-os__stage edge-shell__body" data-kinetic="1">
            <div className="edge-os__stage-body edge-shell__pane">
              <${AnimatePresence} mode="wait" initial=${false}>
                <${M.div} key=${at} className="edge-os__pane edge-shell__pane-motion"
                  initial=${{ opacity: 0, y: 8 }}
                  animate=${{ opacity: 1, y: 0 }}
                  exit=${{ opacity: 0, y: -5 }}
                  transition=${{ type: "spring", stiffness: 420, damping: 36, mass: 0.65 }}>
                  ${stage()}
                <//>
              <//>
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

/* ═══════════ TRANSPORT — Live presence + connections ═══════════════════ */
function Transport({ toast, seat }){
  const surveyOn = !!(seat && seat.surveyAllowed);
  const cmdOn = !!(seat && seat.commandAllowed);
  const reProbeOk = !!(seat && seat.reProbeAllowed);
  const { live, devices, deviceId, setDeviceId } = useEdgeDevices(surveyOn);
  const act = useAction();
  const diagPath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_presence-diagnose", { pathParams: [deviceId] })
    : "";
  const diagLive = useApi(diagPath);
  const loopPath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_board-loop", { pathParams: [deviceId] })
    : "";
  const loopLive = useApi(loopPath || null);
  const linkFacts = useMemo(() => edgeWaveHonestyOf(boardLoopViewOf(loopLive.data)), [loopLive.data]);
  const view = useMemo(() => presenceDiagnoseView(diagLive.data), [diagLive.data]);
  const selected = devices.find((d) => d.id === deviceId) || null;

  const reProbe = async () => {
    if (!cmdOn || !reProbeOk) {
      toast("Re-probe is closed for this seat", "warn"); return;
    }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    const out = await act.run(
      path("post__devices_p_commands", { pathParams: [deviceId] }),
      { method: "POST", body: { cmd: "ping", params: {} } },
    );
    if (!out) {
      toast(act.error || "Re-probe refused — nothing was sent", "bad");
      return;
    }
    toast("Link re-probe ping · " + deviceId, "go");
    live.reload && live.reload();
    diagLive.reload && diagLive.reload();
  };

  return html`
    <section className="edge-live edge-desk-body" data-craft="v370" data-cap="link"
      data-sot="Singular Live transport desk">
      <${EdgeNote} tone="info" title="Sole · link health"
        say="Link health only: presence check and re-probe. Telemetry, reboot, and self-test live on Bench — not here."/>
      ${act.envelope
        ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(act.envelope) || "—"}</p>`
        : null}
      ${!surveyOn
        ? html`<${EdgeNote} tone="warn" title="Read seat closed"
            say="Device register and diagnose need an open read seat on this account."/>`
        : null}
      <${DevicePick} devices=${devices} deviceId=${deviceId} setDeviceId=${setDeviceId}
        loading=${live.loading} error=${live.error}/>

      <${EdgeActRow}>
        <button type="button" className="btn btn--ghost btn--sm edge-os__act"
          disabled=${!surveyOn || !(seat && seat.reReadAllowed)}
          onClick=${()=>{
            if (!surveyOn) { toast("Read seat is closed for this account", "warn"); return; }
            live.reload && live.reload();
            diagLive.reload && diagLive.reload();
            loopLive.reload && loopLive.reload();
            toast("Transport re-read", "go");
          }}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
        <button type="button" className="btn btn--go btn--sm edge-os__act"
          disabled=${!deviceId || act.busy || !cmdOn || !reProbeOk}
          onClick=${reProbe}
          title="Queue a Live path ping">
          <${Ico} d="refresh" w=${13}/> Re-probe link</button>
      <//>
      <p className="edge-cap-hand">
        Need telemetry or reboot?
        <button type="button" className="edge-cap-hand__go" onClick=${() => go("/edge/bench")}>
          Open Bench · Test
        </button>
      </p>

      <${EdgePlate} title="Link health" meta=${deviceId || "no device"}
        sub="Live presence · no invent quiet state">
        ${!deviceId
          ? html`<${EdgeEmpty} title="Pick a Live device" say="Link health waits for a node from the register."/>`
          : html`
              <${EdgeKV}
                rows=${[
                  { k: "Device", v: (selected && selected.label) || deviceId },
                  { k: "Net type", v: (selected && selected.net) || "—" },
                  { k: "Presence", v: (selected && selected.online) ? "online" : "quiet",
                    tone: selected && selected.online ? "hi" : "warn" },
                  { k: "ETH", v: linkFacts.eth || "—" },
                  { k: "Wi-Fi", v: linkFacts.wifi || "—" },
                  { k: "C6", v: linkFacts.c6 || "—" },
                  { k: "MQTT", v: linkFacts.mqtt || "—" },
                  { k: "802.15.4", v: "Hosted does not expose · Soft≠ invent RF" },
                  { k: "LoRa", v: "not on C6 · SX1262 hang only Soft≠ invent" },
                ]}
              />
              ${linkFacts.eth || linkFacts.c6
                ? html`<p className="edge-plate__p">board-loop eth=${linkFacts.eth || "—"} · wifi=${linkFacts.wifi || "—"} · c6=${linkFacts.c6 || "—"} · Soft≠ invent 802.15.4</p>`
                : null}
              ${diagLive.loading && !view
                ? html`<p className="edge-plate__p">Reading Live presence diagnose…</p>`
                : null}
              ${diagLive.error
                ? html`<${Alert} tone="warn" title="Presence diagnose refused" say=${softEdgeErrorOf(diagLive.error)}/>`
                : null}
              ${view
                ? html`<${EdgeKV}
                    rows=${[
                      { k: "Suspect", v: view.suspect || "—", tone: "warn" },
                      { k: "Bucket", v: view.bucket || "—" },
                      { k: "Reason", v: view.reason || "—" },
                      { k: "Next", v: view.nextAction || "—" },
                    ]}
                  />`
                : null}
            `}
      <//>

      ${deviceId && surveyOn ? html`<${ConnectionWatch} deviceId=${deviceId} boardLive=${true} toast=${toast}/>` : null}
    </section>`;
}

/* ═══════════ BORROWING — Live siblings + capability catalog ═════════════ */
function Borrowing({ toast, seat }){
  const surveyOn = !!(seat && seat.surveyAllowed);
  const cmdOn = !!(seat && seat.commandAllowed);
  const pushOk = !!(seat && seat.packagePushAllowed);
  const { live, devices, deviceId, setDeviceId } = useEdgeDevices(surveyOn);
  const act = useAction();
  const sibPath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_siblings-preview", { pathParams: [deviceId] })
    : "";
  const sibLive = useApi(sibPath);
  const loopPath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_board-loop", { pathParams: [deviceId] })
    : "";
  const loopLive = useApi(loopPath || null);
  const borrowFacts = useMemo(() => edgeWaveHonestyOf(boardLoopViewOf(loopLive.data)), [loopLive.data]);
  const capsLive = useApi(surveyOn ? safeEdgePath("get__devices_capabilities") : null);
  const sib = useMemo(() => siblingsOf(sibLive.data), [sibLive.data]);
  const caps = useMemo(() => capabilityCatalogOf(capsLive.data), [capsLive.data]);
  const [picked, setPicked] = useState(() => new Set());
  const [deviceType, setDeviceType] = useState("edge_ai_node");

  const toggleCap = (id) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const pushPackage = async () => {
    if (!cmdOn || !pushOk) {
      toast("Package push is closed for this seat", "warn"); return;
    }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    const claimed = [...picked];
    if (!claimed.length) { toast("Pick at least one capability from the catalog", "warn"); return; }
    const out = await act.run(
      path("post__devices_p_capabilities", { pathParams: [deviceId] }),
      {
        method: "POST",
        body: {
          device_type: deviceType,
          claimed,
          tested: [],
          source: "cmc_edge_borrowing",
        },
      },
    );
    if (!out) {
      toast(/disabled|503/i.test(act.error || "")
        ? "Capability registry is off — nothing was sent"
        : (act.error || "Package push refused — nothing was sent"), "bad");
      return;
    }
    toast("Capability package · " + claimed.length + " claimed on " + deviceId, "go");
    capsLive.reload && capsLive.reload();
  };

  return html`
    <section className="edge-live edge-desk-body" data-craft="v370" data-cap="borrow">
      <${EdgeNote} tone="info" title="Sole · sibling capability push"
        say=${"This desk only pushes registry claims onto a sibling. Compose function packs on Skills. Board flash is Fleet Firmware."
          + (borrowFacts.skill
            ? (" · board-loop skill=" + borrowFacts.skill + " · c6=" + (borrowFacts.c6 || "—") + " · Soft≠ invent 802.15.4 / function MQTT")
            : "")}/>
      ${act.envelope
        ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(act.envelope) || "—"}</p>`
        : null}
      ${!surveyOn
        ? html`<${EdgeNote} tone="warn" title="Read seat closed"
            say="Catalog and siblings need an open read seat on this account."/>`
        : null}
      <${DevicePick} devices=${devices} deviceId=${deviceId} setDeviceId=${setDeviceId}
        loading=${live.loading} error=${live.error}/>

      <${EdgeActRow}>
        <button type="button" className="btn btn--ghost btn--sm edge-os__act"
          disabled=${!surveyOn || !(seat && seat.reReadAllowed)}
          onClick=${()=>{
            if (!surveyOn) { toast("Read seat is closed for this account", "warn"); return; }
            live.reload && live.reload();
            sibLive.reload && sibLive.reload();
            capsLive.reload && capsLive.reload();
            loopLive.reload && loopLive.reload();
            toast("Borrowing re-read", "go");
          }}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
        <button type="button" className="btn btn--go btn--sm edge-os__act"
          disabled=${!deviceId || act.busy || !picked.size || !cmdOn || !pushOk}
          onClick=${pushPackage}
          title="Push selected claims onto this node">
          <${Ico} d="check" w=${13}/> Push capability package</button>
      <//>
      <p className="edge-cap-hand">
        Need to pick skills from the shelf first?
        <button type="button" className="edge-cap-hand__go" onClick=${() => go("/edge/skills")}>
          Open Skills · Compose
        </button>
      </p>

      <div className="edge-plate-grid">
        <${EdgePlate} title="Capability catalog" meta=${caps.persistence || "Live catalog"}
          sub="Push claims on Live registry only">
          ${capsLive.error
            ? html`<${Alert} tone="warn" title="Capabilities refused" say=${softEdgeErrorOf(capsLive.error)}/>`
            : null}
          <label className="edge-field">
            <span>Device type</span>
            <${Input} icon="chip" value=${deviceType} onChange=${(e)=>setDeviceType(e.target.value)}/>
          </label>
          <${EdgeMeters}
            items=${[
              { k: "Bound", v: String(caps.count), on: !!caps.count },
              { k: "Selected", v: String(picked.size), on: !!picked.size },
            ]}
          />
          <div className="edge-chips">
            ${!caps.catalog.length
              ? html`<p className="edge-plate__p">${capsLive.loading ? "Reading…" : "Catalog empty."}</p>`
              : caps.catalog.map((c) => html`
                <button key=${c} type="button" className="edge-chips__item" data-sel=${picked.has(c)?"1":"0"}
                  onClick=${()=>toggleCap(c)}>${c}</button>`)}
          </div>
        <//>

        <${EdgePlate} title="Sibling fan-out" meta="Live siblings-preview">
          ${sibLive.error
            ? html`<${Alert} tone="warn" title="Siblings refused" say=${softEdgeErrorOf(sibLive.error)}/>`
            : null}
          <${EdgeKV}
            rows=${[
              { k: "Zone", v: sib.zone || "—" },
              { k: "Group", v: sib.group || "—" },
              { k: "Fan-out", v: sib.fanout ? "enabled" : "off" },
              { k: "Targets", v: String(sib.targetCount) },
              { k: "Eligible", v: String(sib.eligible) },
            ]}
          />
          <div className="edge-pick edge-pick--compact" role="list">
            ${!sib.targets.length
              ? html`<${EdgeEmpty}
                  title=${sibLive.loading ? "Reading siblings…" : "No sibling targets"}
                  say="No sibling targets on this Live fan-out yet."
                />`
              : sib.targets.slice(0, 40).map((t) => html`
                <div key=${t.id} className="edge-pick__item edge-pick__item--static" role="listitem">
                  <span className="edge-pick__st" data-on="1"/>
                  <span className="edge-pick__main">
                    <b>${t.label}</b>
                    <i>${t.id}</i>
                  </span>
                </div>`)}
          </div>
        <//>
      </div>
    </section>`;
}

/* ═══════════ SESSIONS — Live μDock grants · Page42 remux · Page156 status ═ */
function Sessions({ toast, seat }){
  const surveyOn = !!(seat && seat.surveyAllowed);
  const sessWrite = !!(seat && seat.sessionWriteAllowed);
  const { live, devices, deviceId, setDeviceId } = useEdgeDevices(surveyOn);
  const posture = useApi(surveyOn ? safeEdgePath("get__udock_status") : null);
  const post = posture.data || {};
  /* Fail-closed: only explicit armed opens tunnel controls. */
  const armed = post.armed === true;
  const product = post.product_flag === true;
  const detail = post.detail || null;
  const softDebtN = Array.isArray(post.soft_debt) ? post.soft_debt.length : 0;
  const off = !armed;

  const sessPath = deviceId && armed && surveyOn
    ? safeEdgePath("get__devices_p_live-sessions", { pathParams: [deviceId] })
    : "";
  const sessLive = useApi(sessPath);
  const loopPath = deviceId && surveyOn
    ? safeEdgePath("get__devices_p_board-loop", { pathParams: [deviceId] })
    : "";
  const loopLive = useApi(loopPath || null);
  const w1facts = useMemo(() => edgeWaveHonestyOf(boardLoopViewOf(loopLive.data)), [loopLive.data]);
  const rows = useMemo(() => liveSessionsOf(sessLive.data), [sessLive.data]);
  const udockHon = useMemo(() => udockLiveHonestyOf(w1facts, rows), [w1facts, rows]);
  const [why, setWhy] = useState(SESSION_WHYS[0]);
  const [ttl, setTtl] = useState(SESSION_TTLS[1]);
  const [busy, setBusy] = useState(false);
  const [focusId, setFocusId] = useState("");
  const [focusSess, setFocusSess] = useState(null);
  const honesty = sessLive.data && sessLive.data.honesty;

  useEffect(() => {
    if (!deviceId || !focusId || !armed) {
      setFocusSess(null);
      return undefined;
    }
    let dead = false;
    const pull = async () => {
      const out = await getLiveSession(deviceId, focusId);
      if (dead || !out.ok) return;
      setFocusSess((out.data && out.data.item) || out.data || null);
    };
    pull();
    const t = setInterval(pull, 2000);
    return () => {
      dead = true;
      clearInterval(t);
    };
  }, [deviceId, focusId, armed]);

  useEffect(() => {
    if (!rows.length) { setFocusId(""); return; }
    if (!focusId || !rows.some((r) => r.id === focusId)) {
      setFocusId(rows[0].id);
    }
  }, [rows, focusId]);

  const refresh = () => {
    posture.reload && posture.reload();
    sessLive.reload && sessLive.reload();
    loopLive.reload && loopLive.reload();
    live.reload && live.reload();
  };

  const open = async () => {
    if (!sessWrite) {
      toast("Live session controls are closed for this seat", "warn");
      return;
    }
    if (!armed) {
      toast("Live video is not armed on this site yet", "warn");
      return;
    }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    setBusy(true);
    const out = await openLiveSession(deviceId, {
      operator_reason: why,
      ttl_s: ttl,
    });
    setBusy(false);
    if (!out.ok) { toast(out.error || "Open refused", "bad"); return; }
    const item = (out.data && out.data.item) || out.data || {};
    if (item.session_id) setFocusId(item.session_id);
    toast("Live session opened", "go");
    refresh();
  };

  const revoke = async (sessionId) => {
    if (!sessWrite) {
      toast("Live session controls are closed for this seat", "warn"); return;
    }
    setBusy(true);
    const out = await closeLiveSession(deviceId, sessionId, "CMC Edge · revoke live session");
    setBusy(false);
    if (!out.ok) { toast(out.error || "Revoke refused", "bad"); return; }
    toast("Session closed", "warn");
    if (focusId === sessionId) setFocusId("");
    refresh();
  };

  const renew = async (sessionId) => {
    if (!sessWrite) {
      toast("Live session controls are closed for this seat", "warn"); return;
    }
    setBusy(true);
    const out = await renewLiveSession(deviceId, sessionId, "CMC Edge · renew live session");
    setBusy(false);
    if (!out.ok) { toast(out.error || "Renew refused", "bad"); return; }
    toast("Session renewed", "go");
    refresh();
  };

  return html`
    <section data-cmc-edge-sess="page42" data-cmc-residual="page156-udock"
      className="edge-sess edge-sess--liquid edge-live edge-desk-body" data-craft="v370"
      aria-label="Live video sessions">
      <${EdgeActRow}>
        <button type="button" className="btn btn--ghost btn--sm edge-os__act"
          disabled=${busy || posture.loading || !surveyOn}
          onClick=${()=>{ refresh(); toast("Tunnel re-read", "go"); }}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
      <//>

      <${EdgePlate} title="Live video posture" meta="site grant" sub="Live registry · no invented streams">
        ${posture.loading ? html`<p className="edge-plate__p">Reading posture…</p>` : null}
        ${posture.error ? html`<${Alert} tone="warn" title="Posture refused"
          say=${softEdgeErrorOf(posture.error)}/>` : null}
        ${!posture.loading && !posture.error ? html`
          <${EdgeMeters}
            ariaLabel="Live video posture KPIs"
            items=${[
              { k: "Armed", v: armed ? "yes" : "no", on: armed, em: "enabled Live" },
              { k: "Product", v: product ? "on" : "off", on: product, em: "live product" },
              { k: "Platform", v: post.pg_ready ? "ready" : "—", on: !!post.pg_ready, em: "session store" },
              { k: "Cache", v: post.redis_ready ? "ready" : "—", on: !!post.redis_ready, em: "stream bridge" },
              { k: "Remux", v: w1facts.w1 || "—", on: w1facts.remuxActive, em: "board-loop w1" },
              { k: "Leases", v: String(udockHon.leaseN), on: udockHon.leaseN > 0, em: "host grants" },
              { k: "Media", v: String(udockHon.mediaN), on: udockHon.mediaN > 0, em: "media_played" },
            ]}
          />
          ${detail ? html`<p className="edge-plate__p">${softEdgeErrorOf(detail)}</p>` : null}
          ${softDebtN
            ? html`<p className="edge-plate__p">Some live-video paths are still sealed on this site.</p>`
            : null}
          <p className="edge-plate__foot">
            write ${post.write_allowed === true ? "yes" : "no"}
            · edge act ${sessWrite ? "open" : "sealed"}
            · ${post.posture_label || "live posture"}
          </p>` : null}
      <//>

      ${off
        ? html`<${EdgeNote} tone="warn" title="Live video not armed"
            say=${(post.posture_label || "Live video is not armed")
              + (detail ? " · " + softEdgeErrorOf(detail) : "")
              + ". Open and renew stay unavailable until the site arms live video."}/>`
        : html`<${EdgeNote} tone="info" title="Live video sessions"
            say=${udockHon.say + " · Armed is a site grant. Soft≠ treat armed as remux playing."}/>`}

      ${honesty ? html`<p className="edge-plate__p">${softEdgeErrorOf(honesty)}</p>` : null}
      <${DevicePick} devices=${devices} deviceId=${deviceId} setDeviceId=${setDeviceId}
        loading=${live.loading} error=${live.error}/>

      <${EdgePlate} title="Open sessions"
        meta=${rows.length ? (rows.length + " running") : (armed ? "Empty" : "gated")}>
        ${off ? html`<${EdgeEmpty} title="Waiting for armed posture"
          say="Session list waits until live video is armed."/>` : null}
        ${sessLive.error
          ? html`<${Alert} tone="warn" title="Sessions refused" say=${softEdgeErrorOf(sessLive.error)}/>`
          : null}
        ${armed && !rows.length
          ? html`<${EdgeEmpty}
              title=${sessLive.loading ? "Reading sessions…" : "No Live sessions"}
              say="Open a session below when act seat is open."
            />`
          : null}
        ${armed && rows.length ? html`<div className="edge-sess-list">
              ${rows.map((s) => html`
                <div key=${s.id} className="edge-sess-list__row"
                  data-focus=${focusId===s.id?"1":"0"}>
                  <button type="button" className="edge-sess-list__main"
                    onClick=${()=>setFocusId(s.id)}>
                    <b>${s.id}</b>
                    <i>${s.status}${s.mediaPlayed ? " · media_played" : " · lease-only"}${s.camera ? " · " + s.camera : ""}</i>
                  </button>
                  <span className="edge-sess-list__ttl" data-tone=${s.leftS != null && s.leftS < 60 ? "warn" : undefined}>
                    ${fmtLeft(s.leftS)} left
                  </span>
                  <div className="edge-sess-list__acts">
                    <button className="btn btn--ghost btn--sm" disabled=${busy || !sessWrite}
                      onClick=${()=>renew(s.id)}>Renew</button>
                    <button className="btn btn--danger btn--sm" disabled=${busy || !sessWrite}
                      onClick=${()=>revoke(s.id)}>Close session</button>
                  </div>
                </div>`)}
            </div>` : null}
      <//>

      ${deviceId && focusId && armed ? html`
        <${EdgePlate} title="Live grant" meta="WebCodecs · remux AU">
          <${GrantLatticePlayer}
            deviceId=${deviceId}
            sessionId=${focusId}
            grant=${focusSess && focusSess.grant_lattice}
            mediaPlayed=${!!(focusSess && focusSess.media_played)}
            surface="desk"
            toast=${toast}/>
        <//>
        <${EdgePlate} title="Remux bridge" meta="Bridge bytes">
          <${RemuxPullDesk} deviceId=${deviceId} sessionId=${focusId} toast=${toast} auto=${true}/>
        <//>` : null}

      <${EdgePlate} title="Open a session" meta="New session"
        sub=${off || !sessWrite ? "Closed for this seat" : "Audited live grant"}>
        <label className="edge-field">
          <span>Why · audited (≥8 chars)</span>
          <div className="edge-chips edge-chips--wrap">
            ${SESSION_WHYS.map((r) => html`
              <button key=${r} type="button"
                className="edge-chips__item" data-sel=${why===r?"1":"0"}
                onClick=${()=>setWhy(r)} disabled=${off || !sessWrite}>${r}</button>`)}
          </div>
          <${Input} icon="book" value=${why} onChange=${(e)=>setWhy(e.target.value)}
            disabled=${off || !sessWrite}/>
        </label>
        <label className="edge-field">
          <span>Time limit</span>
          <div className="edge-chips">
            ${SESSION_TTLS.map((t) => html`
              <button key=${t} type="button"
                className="edge-chips__item" data-sel=${ttl===t?"1":"0"}
                onClick=${()=>setTtl(t)} disabled=${off || !sessWrite}>${fmtLeft(t)}</button>`)}
          </div>
        </label>
        <${EdgeActRow}>
          <button className="btn btn--go btn--sm"
            disabled=${off || !deviceId || busy || !sessWrite || String(why||"").trim().length < 8}
            onClick=${open}>
            <${Ico} d="eye" w=${13}/> Open</button>
        <//>
      <//>
    </section>`;
}

/* ═══════════ WORKBENCH — Live jobs + control + board telemetry Page50 ═══ */
function Workbench({ toast, seat }){
  const surveyOn = !!(seat && seat.surveyAllowed);
  const wbWrite = !!(seat && seat.workbenchWriteAllowed);
  const { live, devices, deviceId, setDeviceId } = useEdgeDevices(surveyOn);
  const act = useAction();
  const jobsPath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_scheduled-jobs", { pathParams: [deviceId] })
    : "";
  const telePath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_board-telemetry", { pathParams: [deviceId] })
    : "";
  const loopPath = surveyOn && deviceId
    ? safeEdgePath("get__devices_p_board-loop", { pathParams: [deviceId] })
    : "";
  const jobsLive = useApi(jobsPath);
  const teleLive = useApi(telePath);
  const loopLive = useApi(loopPath || null);
  const benchFacts = useMemo(() => edgeWaveHonestyOf(boardLoopViewOf(loopLive.data)), [loopLive.data]);
  const jobs = useMemo(() => scheduledJobsOf(jobsLive.data), [jobsLive.data]);
  const tele = useMemo(() => boardTelemetryViewOf(teleLive.data), [teleLive.data]);
  const [delayS, setDelayS] = useState(300);

  const requestTelemetry = async () => {
    if (!wbWrite) { toast("Workbench is closed for this seat", "warn"); return; }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    const out = await act.run(
      path("post__devices_p_commands", { pathParams: [deviceId] }),
      { method: "POST", body: { cmd: "ping", params: {} } },
    );
    if (!out) {
      toast(softEdgeErrorOf(act.error) || "Telemetry request refused — nothing was sent", "bad");
      return;
    }
    toast("Telemetry request ping · " + deviceId, "go");
    teleLive.reload && teleLive.reload();
    jobsLive.reload && jobsLive.reload();
  };

  const scheduleReboot = async () => {
    if (!wbWrite) { toast("Workbench is closed for this seat", "warn"); return; }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    const d = devices.find((x) => x.id === deviceId);
    const ok = await confirmDeviceCommand({
      kind: "schedule_reboot",
      targets: [{ id: deviceId, label: (d && d.label) || deviceId }],
      detail: "Schedules a reboot in about " + delayS + " seconds on this machine.",
      confirmLabel: "Schedule reboot",
    });
    if (!ok) return;
    const out = await act.run(
      path("post__devices_p_schedule-reboot", { pathParams: [deviceId] }),
      { method: "POST", body: { delay_s: delayS } },
    );
    if (!out) {
      toast(softEdgeErrorOf(act.error) || "Schedule reboot refused", "bad");
      return;
    }
    const jid = out.job_id || out.id || "";
    toast(jid
      ? ("Reboot scheduled · " + jid)
      : ("Reboot scheduled · " + deviceId), "go");
    jobsLive.reload && jobsLive.reload();
  };

  const selfTest = async () => {
    if (!wbWrite) { toast("Workbench is closed for this seat", "warn"); return; }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    const d = devices.find((x) => x.id === deviceId);
    const ok = await confirmDeviceCommand({
      kind: "self_test",
      targets: [{ id: deviceId, label: (d && d.label) || deviceId }],
      detail: "Runs the on-device self-test path.",
      confirmLabel: "Run self-test",
    });
    if (!ok) return;
    const out = await act.run(
      path("post__devices_p_self-test", { pathParams: [deviceId] }),
      { method: "POST", body: {} },
    );
    if (!out) {
      toast(softEdgeErrorOf(act.error) || "Self-test refused — nothing was sent", "bad");
      return;
    }
    toast("Self-test sent · " + deviceId, "go");
  };

  const alertCmd = async (on) => {
    if (!wbWrite) { toast("Workbench is closed for this seat", "warn"); return; }
    if (!deviceId) { toast("Pick a Live device", "warn"); return; }
    const d = devices.find((x) => x.id === deviceId);
    const ok = await confirmDeviceCommand({
      kind: on ? "alert_on" : "alert_off",
      targets: [{ id: deviceId, label: (d && d.label) || deviceId }],
      detail: on ? "Arms the siren on this machine." : "Stops the siren on this machine.",
      confirmLabel: on ? "Sound siren" : "Silence siren",
    });
    if (!ok) return;
    const op = on ? "post__devices_p_alert_on" : "post__devices_p_alert_off";
    const out = await act.run(
      path(op, { pathParams: [deviceId] }),
      { method: "POST", body: {} },
    );
    if (!out) {
      toast(act.error || ((on ? "Alert on" : "Alert off") + " refused"), "bad");
      return;
    }
    toast((on ? "Siren armed" : "Siren stopped") + " · " + deviceId, on ? "go" : "warn");
  };

  return html`
    <div className="edge-bench edge-bench--liquid edge-live edge-desk-body" data-craft="v370"
      data-cmc-edge-bench="page50" data-cmc-page="page104-edge-bench" data-cap="bench">
      <${EdgeNote} tone="info" title="Node test workbench"
        say=${"Telemetry, self-test, scheduled reboot, and per-device siren are Live commands. Link path health is the Link desk. Site-wide alarm silence is Ops Alerts."
          + (benchFacts.usb || benchFacts.dsi
            ? (" · usb=" + (benchFacts.usb || "—") + " · dsi=" + (benchFacts.dsi || "—") + " · Soft≠ invent VBUS GPIO · Soft≠ empty DSI init")
            : "")}/>
      ${act.envelope
        ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(act.envelope) || "—"}</p>`
        : null}
      <${DevicePick} devices=${devices} deviceId=${deviceId} setDeviceId=${setDeviceId}
        loading=${live.loading} error=${live.error}/>

      <${EdgeActRow}>
        <button type="button" className="btn btn--ghost btn--sm edge-os__act"
          disabled=${act.busy || live.loading || !surveyOn}
          onClick=${()=>{
            live.reload && live.reload();
            jobsLive.reload && jobsLive.reload();
            teleLive.reload && teleLive.reload();
            loopLive.reload && loopLive.reload();
            toast("Workbench re-read", "go");
          }}>
          <${Ico} d="refresh" w=${13}/> Re-read</button>
        <button type="button" className="btn btn--go btn--sm edge-os__act"
          disabled=${!deviceId || act.busy || !wbWrite}
          onClick=${requestTelemetry}
          title="Pull board telemetry envelope">
          <${Ico} d="gauge" w=${13}/>Request telemetry</button>
        <button type="button" className="btn btn--ghost btn--sm edge-os__act"
          disabled=${!deviceId || act.busy || !wbWrite}
          onClick=${selfTest}
          title="Queue node self-test">Self-test</button>
        <button type="button" className="btn btn--go btn--sm edge-os__act"
          disabled=${!deviceId || act.busy || !wbWrite}
          onClick=${()=>alertCmd(true)}
          title="Arm siren on this device only">Device siren on</button>
        <button type="button" className="btn btn--danger btn--sm edge-os__act"
          disabled=${!deviceId || act.busy || !wbWrite}
          onClick=${()=>alertCmd(false)}
          title="Clear siren on this device only">Device siren off</button>
      <//>
      <p className="edge-cap-hand">
        Path probe only?
        <button type="button" className="edge-cap-hand__go" onClick=${() => go("/edge/link")}>Open Link · Probe</button>
        · Site alarms?
        <button type="button" className="edge-cap-hand__go" onClick=${() => go("/ops/alarms")}>Ops Alerts</button>
      </p>

      <div className="edge-plate-grid">
        <${EdgePlate} title="Schedule reboot" meta="Live queue"
          sub="Live enqueue only — a job id means it actually landed">
          <label className="edge-field">
            <span>Delay · seconds until Live reboot</span>
            <div className="edge-chips">
              ${REBOOT_DELAYS.map((t) => html`
                <button key=${t} type="button"
                  className="edge-chips__item" data-sel=${delayS===t?"1":"0"}
                  disabled=${!wbWrite}
                  onClick=${()=>setDelayS(t)}>${fmtLeft(t)}</button>`)}
            </div>
          </label>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${!deviceId || act.busy || !wbWrite}
            onClick=${scheduleReboot}>Schedule reboot</button>
        <//>

        <${EdgePlate} title="Board telemetry" meta="fresh board envelope"
          sub="Live board envelope only">
          ${teleLive.loading && !teleLive.data && !teleLive.error
            ? html`<p className="edge-plate__p">Reading Live telemetry…</p>`
            : null}
          ${teleLive.error
            ? html`<${EdgeNote} tone="info" title="Telemetry not available on this device"
                say=${softEdgeErrorOf(teleLive.error)}/>`
            : tele
              ? html`<${EdgeKV}
                  rows=${[
                    { k: "Profile", v: tele.profile },
                    { k: "Temp °C", v: tele.temp != null ? String(tele.temp) : "—" },
                    { k: "Heap free", v: tele.heap != null ? String(tele.heap) : "—" },
                    { k: "Uptime s", v: tele.uptime != null ? String(tele.uptime) : "—" },
                    { k: "Keys", v: tele.keys.join(", ") || "—" },
                  ]}
                />`
              : html`<${EdgeEmpty} title="No telemetry loaded" say="Request telemetry to ping the node."/>`}
        <//>
      </div>

      <${EdgePlate} title="Scheduled jobs" meta="planned node work">
        ${jobsLive.error
          ? html`<${Alert} tone="warn" title="Jobs refused" say=${softEdgeErrorOf(jobsLive.error)}/>`
          : null}
        ${!jobs.length
          ? html`<${EdgeEmpty}
              title=${jobsLive.loading ? "Reading jobs…" : "No Live scheduled jobs"}
              say="Jobs appear after schedule reboot succeeds."
            />`
          : html`<div className="edge-pick edge-pick--compact" role="list">
              ${jobs.map((j) => html`
                <div key=${j.id} className="edge-pick__item edge-pick__item--static" role="listitem">
                  <span className="edge-pick__st" data-on="1"/>
                  <span className="edge-pick__main">
                    <b>${j.title}</b>
                    <i>${j.id} · cmd ${j.cmd}${j.executeAt != null ? (" · at " + j.executeAt) : ""}</i>
                  </span>
                  <em className="edge-pick__tag">${j.status}</em>
                </div>`)}
            </div>`}
      <//>

      ${deviceId && surveyOn ? html`<${ConnectionWatch} deviceId=${deviceId} boardLive=${true} toast=${toast}/>` : null}
    </div>`;
}
